import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import { SETTINGS_ENTRIES, isSettingsEntryVisible } from "./settingsRegistry";

const STANDALONE_SETTINGS = ["billing", "onboarding", "internal-billing"];
// Reached from the header tabs, so a Back button would only duplicate them.
const HEADER_SETTINGS = ["store-groups"];
const SHARED_SETTINGS = ["roles", "training", "check-ins"];
const ADMIN_ROLES = ["company_admin", "rightwork_internal"];

export default function SettingsDisplay(props) {
  const [selectedId, setSelectedId] = React.useState(() =>
    SETTINGS_ENTRIES.find((entry) => entry.legacyTab === props.initialTab)?.id || null
  );
  const [switching, setSwitching] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dirty, setDirty] = React.useState(false);
  const dirtyEditors = React.useRef(new Set());
  const reportDirty = React.useCallback((key, value) => {
    if (value) dirtyEditors.current.add(key);
    else dirtyEditors.current.delete(key);
    const hasChanges = dirtyEditors.current.size > 0;
    setDirty(hasChanges);
    props.onSettingsDirtyChange(hasChanges);
  }, [props.onSettingsDirtyChange]);
  // Editors inside the secondary (unmount-on-navigate) view, tracked apart
  // so an in-page tab change can ask before discarding them.
  const secondaryEditors = React.useRef(new Set());
  const reportSecondaryDirty = React.useCallback((key, value) => {
    if (value) secondaryEditors.current.add(key);
    else secondaryEditors.current.delete(key);
    reportDirty(key, value);
  }, [reportDirty]);

  React.useEffect(() => {
    const beforeUnload = (event) => {
      if (!dirtyEditors.current.size) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      props.onSettingsDirtyChange(false);
    };
  }, [props.onSettingsDirtyChange]);

  const isAdmin = ADMIN_ROLES.includes(props.permissionRole);
  const visible = SETTINGS_ENTRIES.filter((entry) => isSettingsEntryVisible(entry, props));
  const selected = visible.find((entry) => entry.id === selectedId);
  const showingIntegrations = isAdmin && selectedId === "integrations";
  const showingOverview = isAdmin && !showingIntegrations && (!selected || selected.id === "locations" || SHARED_SETTINGS.includes(selected.id));
  const store = props.storeNames?.find((location) => Number(location.id) === Number(props.homeStore));
  const companyId = store?.company || props.homeCompany;
  const companies = Array.from(new Map((props.storeNames || []).map((location) => [
    location.company,
    { id: location.company, name: location.company_info?.company_name || "Company" },
  ])).values());

  const navigate = (id) => {
    // The secondary view unmounts on a tab change (the overview stays
    // mounted and keeps its drafts), so leaving it with unsaved edits asks
    // first, the same as a company switch does.
    if (id !== selectedId && secondaryEditors.current.size && !window.confirm("Discard unsaved changes in this settings view?")) return;
    setSelectedId(id);
    window.scrollTo(0, 0);
  };
  // Settings header navigation is local, so the app-level tab may still be
  // the destination requested by an onboarding action. Navigate locally to
  // avoid a no-op on repeat visits and preserve the loaded overview.
  const navigateTab = (tab) => {
    const entry = visible.find((item) => item.legacyTab === tab);
    if (entry) navigate(entry.id);
    else props.onNavigateTab?.(tab);
  };
  const changeStore = async (storeId) => {
    const target = props.storeNames?.find((location) => Number(location.id) === Number(storeId));
    const changingCompany = target && Number(target.company) !== Number(companyId);
    const membership = props.storeOptions?.find((member) => Number(member.store) === Number(storeId));
    const losingAdminAccess = isAdmin && membership && !ADMIN_ROLES.includes(membership.permission_type);
    if ((changingCompany || losingAdminAccess) && dirtyEditors.current.size && !window.confirm("Discard unsaved settings changes and switch location or company?")) return;
    setSwitching(true);
    setError("");
    try {
      await props.change_home_store(storeId);
    } catch (_) {
      setError("Couldn't switch location or company. Please try again.");
    } finally {
      setSwitching(false);
    }
  };
  const changeCompany = (value) => {
    const locations = (props.storeNames || []).filter((location) => Number(location.company) === Number(value));
    // Prefer an administrator membership for users with different roles by location.
    const target = locations.find((location) => props.storeOptions?.some((member) =>
      Number(member.store) === Number(location.id) && ADMIN_ROLES.includes(member.permission_type)
    )) || locations[0];
    if (target) return changeStore(target.id);
  };
  const tabs = isAdmin
    ? [{ id: "locations", title: "Settings" }, { id: "store-groups", title: "Store groups" }, { id: "integrations", title: "Integrations" }, ...visible.filter((entry) => STANDALONE_SETTINGS.includes(entry.id)).map((entry) => ({ ...entry, title: entry.id === "onboarding" ? "Onboarding" : entry.title }))]
    : visible.filter((entry) => ["roles", "training", ...STANDALONE_SETTINGS].includes(entry.id));
  const isSelected = (id) => id === "locations" ? showingOverview : selectedId === id;
  const title = showingOverview ? "Settings" : showingIntegrations ? "Integrations" : selected?.title || "Settings";

  return <div className="min-h-screen bg-slate-50 text-slate-900">
    <header className="border-b border-slate-200 bg-white px-8 py-6 sm:px-4">
      <div className="flex items-start justify-between gap-6 md:flex-col">
        <div>
          {!showingOverview && !showingIntegrations && !STANDALONE_SETTINGS.includes(selected?.id) && !HEADER_SETTINGS.includes(selected?.id) && isAdmin && <button
            type="button"
            onClick={() => navigate(null)}
            className="mb-3 flex items-center gap-2 rounded text-sm font-medium text-violet-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600"
          ><ArrowBackIcon fontSize="small" />Back to settings</button>}
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="mt-2 text-sm text-slate-500">
            {showingOverview
              ? "Compare location features and manage company-wide settings. Select a cell to edit."
              : showingIntegrations ? "Manage connected systems and linked locations." : selected?.description || "Manage your company's settings."}
          </p>
        </div>
        <div className="w-64 md:w-full">
          <label id="settings-company-label" htmlFor="settings-company" className="mb-1.5 block text-xs font-semibold text-slate-600">Company</label>
          <RightworkSelect
            id="settings-company"
            value={companyId || ""}
            disabled={switching}
            onChange={(event) => changeCompany(event.target.value)}
            options={companies.map((company) => ({ value: company.id, label: company.name }))}
          />
          {error && <p role="alert" className="mt-2 text-sm text-rose-700">{error}</p>}
          {dirty && <p className="mt-2 text-xs text-amber-700">Unsaved changes in settings</p>}
        </div>
      </div>
      <nav aria-label="Settings views" className="mt-5 flex flex-wrap gap-2">
        {tabs.map((entry) => <button
          key={entry.id}
          type="button"
          aria-pressed={isSelected(entry.id)}
          onClick={() => navigate(entry.id)}
          className={`rounded-lg px-3 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 ${(isSelected(entry.id)) ? "bg-violet-50 text-violet-800" : "text-slate-600 hover:bg-slate-100"}`}
        >{entry.title}</button>)}
      </nav>
    </header>
    {/* Keep the comparison view mounted across secondary pages. Expansion,
        copy/paste state, and unsaved inline drafts survive the return journey. */}
    {isAdmin && <div hidden={!showingOverview && !showingIntegrations} aria-busy={switching} className={switching ? "pointer-events-none opacity-50" : ""} key={`overview:${companyId}`}>
      {props.renderLegacy("locationsIntegrationsHoursSettings", {
        settingsView: showingIntegrations ? "Integrations" : "Locations",
        settingsOverview: true,
        settingsActive: showingOverview || showingIntegrations,
        onSettingsAreaChange: navigate,
        companySettingsEditor: SHARED_SETTINGS.includes(selectedId) ? selectedId : undefined,
        companySettingsBucket: selectedId === "roles" || selectedId === "check-ins" ? "team_management" : selectedId === "training" ? "training" : undefined,
        settingsCompanyId: companyId,
        settingsChangeHomeStore: changeStore,
        onSettingsCompanyChange: changeCompany,
        onFeatureSettingsDirtyChange: reportDirty,
      })}
    </div>}
    {!showingOverview && !showingIntegrations && selected && selected.id !== "locations" && <div className="bg-white" key={`${selected.id}:${companyId}`}>
      {props.renderLegacy(selected.legacyTab, {
        settingsNavigateTab: navigateTab,
        settingsView: selected.settingsView,
        settingsCompanyId: companyId,
        settingsChangeHomeStore: changeStore,
        onSettingsCompanyChange: changeCompany,
        onFeatureSettingsDirtyChange: reportSecondaryDirty,
      })}
    </div>}
    {!isAdmin && !selected && <p className="p-8 text-sm text-slate-500">
      {tabs.length ? "Choose a settings view above." : "No settings are available for your current access level."}
    </p>}
  </div>;
}
